import { useState } from 'react';
import { X } from 'lucide-react';
import type { Agent, Studio, StudioId } from '@/domain/types';

const ROLES: Array<{ value: Agent['role']; label: string }> = [
  { value: 'bookings-agent', label: 'Bookings agent' },
  { value: 'studio-manager', label: 'Studio manager' },
  { value: 'operations-lead', label: 'Operations lead' },
  { value: 'owner', label: 'Owner' },
  // 'apprentice' hidden for now — role kept in the type/DB, just not assignable here.
  { value: 'artist', label: 'Artist' },
  { value: 'finance', label: 'Finance / Accounts' },
  { value: 'finance-manager', label: 'Finance Manager' },
  // 75n6: the Ops sub-role seats.
  { value: 'confirmations', label: 'Confirmations' },
  { value: 'sweeper', label: 'Sweeper' },
  { value: 'floor', label: 'Floor manager' },
];

interface Props {
  agent: Agent;
  studios: Studio[];
  onCancel: () => void;
  onSave: (agent: Agent) => void;
}

export default function EditTeamMemberModal({ agent, studios, onCancel, onSave }: Props) {
  const [role, setRole] = useState<Agent['role']>(agent.role);
  const [assigned, setAssigned] = useState<StudioId[]>(agent.assignedStudios);

  const toggleStudio = (id: StudioId) =>
    setAssigned((cur) => (cur.includes(id) ? cur.filter((s) => s !== id) : [...cur, id]));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-black/70 p-4"
      data-testid="team-edit-modal"
      onClick={onCancel}
    >
      <div
        className="panel p-6 w-full max-w-md space-y-4 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between">
          <div>
            <p className="label-small-caps text-gold-bright">Edit member</p>
            <h2 className="heading-display text-2xl text-cream mt-1">
              {agent.firstName} {agent.lastName}
            </h2>
          </div>
          <button type="button" onClick={onCancel} className="btn-quiet" aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <label className="block text-[10px] font-inter uppercase tracking-widest text-cream-soft">
          Role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Agent['role'])}
            className="mt-1 block w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
            data-testid="team-edit-role"
            autoFocus
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>

        <div>
          <p className="text-[10px] font-inter uppercase tracking-widest text-cream-soft mb-2">
            Assigned studios
          </p>
          <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
            {studios.map((s) => (
              <label key={s.id} className="flex items-center gap-1.5 text-xs text-cream-soft">
                <input
                  type="checkbox"
                  checked={assigned.includes(s.id)}
                  onChange={() => toggleStudio(s.id)}
                />
                {s.brand} · {s.name}
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="btn-quiet text-xs">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave({ ...agent, role, assignedStudios: assigned })}
            className="btn-primary text-xs"
            data-testid="team-edit-save"
          >
            Save member
          </button>
        </div>
      </div>
    </div>
  );
}
